import { useEffect, useState } from 'react';

/**
 * Resolve a host-page element for a React Portal, re-resolving when the
 * selector changes.
 *
 * The three-state return is the point: a caller has to tell "the host never
 * asked for an external mount" apart from "the host asked and the selector
 * matched nothing", because the right fallback differs per widget.
 *
 *   `undefined` — no selector requested. Render wherever you normally would.
 *   `null`      — selector given, nothing matched. The host meant to place
 *                 this somewhere; a theme typo or markup that has not loaded
 *                 yet is the usual cause. What to do is the caller's call.
 *   element     — portal into it.
 */
export function useExternalMount(selector: string | undefined): HTMLElement | null | undefined {
    const [el, setEl] = useState<HTMLElement | null | undefined>(undefined);

    useEffect(() => {
        if (!selector) {
            setEl(undefined);
            return;
        }
        if (typeof document === 'undefined') {
            setEl(null);
            return;
        }
        setEl(document.querySelector<HTMLElement>(selector) ?? null);
    }, [selector]);

    return el;
}
